import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Loader2, User, MapPin, Phone, Building, Clock, Package, 
  ChevronDown, ChevronUp, Settings, AlertTriangle, Mail, RotateCcw, Pencil, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { format, isMonday, nextMonday, getDay, setHours, setMinutes } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import ClientLayout from '../components/ClientLayout';

const API_URL = window.location.origin;
const UK_TIMEZONE = 'Europe/London';

const Account = () => {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [reportingItem, setReportingItem] = useState(null);
  const [profileOptions, setProfileOptions] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation_name: '',
    mobile_number: '',
    delivery_address: '',
    password: '',
    catering_plan: 'Small Meals',
    hte_meal_type: 'Standard Sharing/Catering Packs',
    delivery_option: 'Standard (Pre-12pm)'
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        organisation_name: user.organisation_name || '',
        mobile_number: user.mobile_number || '',
        delivery_address: user.delivery_address || '',
        password: '',
        catering_plan: user.catering_plan || 'Small Meals',
        hte_meal_type: user.hte_meal_type || 'Standard Sharing/Catering Packs',
        delivery_option: user.delivery_option || 'Standard (Pre-12pm)'
      });
    }
  }, [user]);

  useEffect(() => {
    fetchOrders();
    fetchOptions();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOptions = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/options`, { withCredentials: true });
      setProfileOptions(data);
    } catch (e) {
      // Error handled silently
    }
  };

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/orders`, { withCredentials: true });
      setOrders(data);
    } catch (e) {
      // Error handled silently
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleReorder = (order) => {
    navigate('/order', { state: { reorderFrom: order } });
  };

  const isOrderEditable = (order) => {
    const deliveryWeek = order.delivery_week;
    if (!deliveryWeek) return false;
    const deliveryMonday = new Date(deliveryWeek + 'T00:00:00');
    // Cutoff is Thursday before delivery week at 14:00 UK time
    const nowUTC = new Date();
    const nowUK = toZonedTime(nowUTC, UK_TIMEZONE);
    // Thursday before the delivery Monday = Monday - 4 days
    const cutoffThursday = new Date(deliveryMonday);
    cutoffThursday.setDate(deliveryMonday.getDate() - 4);
    const cutoffUK = toZonedTime(cutoffThursday, UK_TIMEZONE);
    cutoffUK.setHours(14, 0, 0, 0);
    return nowUK < cutoffUK;
  };

  const handleEditOrder = (order) => {
    if (!isOrderEditable(order)) {
      toast.error('Editing deadline passed. Orders can only be edited before Thursday 2pm (UK time).');
      return;
    }
    navigate('/order', { state: { editOrder: order } });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const updateData = { ...formData };
    if (!updateData.password) {
      delete updateData.password;
    }

    const result = await updateProfile(updateData);
    
    if (result.success) {
      toast.success('Profile updated successfully');
      setFormData(prev => ({ ...prev, password: '' }));
    } else {
      toast.error(result.error);
    }
    setLoading(false);
  };

  const handleReportItem = async (orderId, itemIndex, reportType, notes = '') => {
    try {
      await axios.post(`${API_URL}/api/orders/${orderId}/report-item`, {
        item_index: itemIndex,
        report_type: reportType,
        notes: notes
      }, { withCredentials: true });
      toast.success(`Item reported as ${reportType}`);
      setReportingItem(null);
      fetchOrders();
    } catch (e) {
      // Error handled silently
      toast.error('Failed to report item');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return statusColors[status] || 'bg-slate-100 text-slate-700';
  };

  const getItemReport = (order, itemIndex) => {
    const reports = order.item_reports || [];
    return reports.find(r => r.item_index === itemIndex);
  };

  return (
    <ClientLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-6 sm:mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
          My Account
        </h1>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-slate-100 p-1 rounded-full w-full sm:w-auto flex">
            <TabsTrigger 
              value="profile" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-3 sm:px-6 flex-1 sm:flex-none text-xs sm:text-sm"
              data-testid="account-profile-tab"
            >
              <User className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Profile</span>
              <span className="sm:hidden">Profile</span>
            </TabsTrigger>
            <TabsTrigger 
              value="preferences" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-3 sm:px-6 flex-1 sm:flex-none text-xs sm:text-sm"
              data-testid="account-preferences-tab"
            >
              <Settings className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Meal & Delivery</span>
              <span className="sm:hidden">Prefs</span>
            </TabsTrigger>
            <TabsTrigger 
              value="orders" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm px-3 sm:px-6 flex-1 sm:flex-none text-xs sm:text-sm"
              data-testid="account-orders-tab"
            >
              <Package className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Order History</span>
              <span className="sm:hidden">Orders</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="border-slate-200/60 shadow-sm">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Profile Details</CardTitle>
                <CardDescription>Manage your contact information and delivery address</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Username (read-only) */}
                  {user?.username && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2 text-slate-500">
                        <User className="w-4 h-4" />
                        Username (login ID)
                      </Label>
                      <Input
                        value={user.username}
                        disabled
                        className="rounded-xl bg-slate-50 text-slate-500"
                        data-testid="profile-username-display"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-500" />
                        Contact Name
                      </Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="rounded-xl"
                        data-testid="profile-name-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-slate-500" />
                        Email (for notifications)
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="rounded-xl"
                        placeholder="your@email.com"
                        data-testid="profile-email-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="organisation" className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-slate-500" />
                        Organisation Name
                      </Label>
                      <Input
                        id="organisation"
                        value={formData.organisation_name}
                        onChange={(e) => setFormData({ ...formData, organisation_name: e.target.value })}
                        className="rounded-xl"
                        data-testid="profile-organisation-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="mobile" className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-slate-500" />
                        Mobile Number
                      </Label>
                      <Input
                        id="mobile"
                        value={formData.mobile_number}
                        onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                        className="rounded-xl"
                        placeholder="+44 7XXX XXXXXX"
                        data-testid="profile-mobile-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="address" className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      Delivery Address
                    </Label>
                    <textarea
                      id="address"
                      value={formData.delivery_address}
                      onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                      className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter your delivery address"
                      data-testid="profile-address-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="flex items-center gap-2">
                      Change Password (leave blank to keep current)
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="rounded-xl"
                      placeholder="New password"
                      data-testid="profile-password-input"
                    />
                  </div>

                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      className="rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-all duration-200"
                      disabled={loading}
                      data-testid="profile-save-button"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Meal Preferences Tab */}
          <TabsContent value="preferences">
            <Card className="border-slate-200/60 shadow-sm">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Meal & Delivery Preferences</CardTitle>
                <CardDescription>Configure your catering plan, meal type, and delivery preferences. These settings apply to all your orders.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Catering Service Plan */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Catering Service Plan</Label>
                      <p className="text-sm text-slate-500 mt-1">Select your meal portion size</p>
                    </div>
                    <Select 
                      value={formData.catering_plan} 
                      onValueChange={(value) => setFormData({ ...formData, catering_plan: value })}
                    >
                      <SelectTrigger className="rounded-xl h-14" data-testid="catering-plan-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(profileOptions?.catering_plans || ['Small Meals', 'Large Meals']).map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            <span className="font-medium">{opt}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Heat to Eat Meal Type */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Heat to Eat Meal Type</Label>
                      <p className="text-sm text-slate-500 mt-1">Choose your preferred serving format for Heat to Eat meals</p>
                    </div>
                    <Select 
                      value={formData.hte_meal_type} 
                      onValueChange={(value) => setFormData({ ...formData, hte_meal_type: value })}
                    >
                      <SelectTrigger className="rounded-xl h-14" data-testid="hte-meal-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(profileOptions?.hte_meal_types || ['Standard Sharing/Catering Packs', 'Individual Heat to Eat Meals']).map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            <span className="font-medium">{opt}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Delivery Option */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-base font-medium">Delivery Option</Label>
                      <p className="text-sm text-slate-500 mt-1">Choose your preferred delivery time slot</p>
                    </div>
                    <Select 
                      value={formData.delivery_option} 
                      onValueChange={(value) => setFormData({ ...formData, delivery_option: value })}
                    >
                      <SelectTrigger className="rounded-xl h-14" data-testid="delivery-option-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(profileOptions?.delivery_options || ['Standard (Pre-12pm)', 'Premium (Pre-10:30am)']).map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            <span className="font-medium">{opt}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      className="rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-all duration-200"
                      disabled={loading}
                      data-testid="preferences-save-button"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Preferences'
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Order History Tab */}
          <TabsContent value="orders">
            <Card className="border-slate-200/60 shadow-sm">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Order History</CardTitle>
                <CardDescription>View your past and upcoming orders. Click to expand and report issues.</CardDescription>
              </CardHeader>
              <CardContent>
                {ordersLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No orders yet</p>
                    <p className="text-sm text-slate-400 mt-1">Your orders will appear here</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => {
                      const isExpanded = expandedOrder === order.id;
                      return (
                        <div 
                          key={order.id} 
                          className="border border-slate-200 rounded-xl overflow-hidden transition-all"
                          data-testid={`order-item-${order.id}`}
                        >
                          {/* Order Header - Clickable */}
                          <button
                            onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                            className="w-full p-4 flex items-start justify-between hover:bg-slate-50 transition-colors text-left"
                            data-testid={`order-toggle-${order.id}`}
                          >
                            <div className="space-y-1 min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                <span className="font-medium text-slate-900 text-sm sm:text-base">
                                  {order.delivery_week ? format(new Date(order.delivery_week), 'EEEE, d MMMM yyyy') : 'N/A'}
                                </span>
                                <Badge className={`${getStatusBadge(order.status)} capitalize text-xs`}>
                                  {order.status}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500">
                                <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                                Ordered: {order.created_at ? format(new Date(order.created_at), 'dd MMM yyyy, HH:mm') : 'N/A'}
                              </div>
                              <div className="text-xs sm:text-sm text-slate-600 mt-1">
                                <span className="font-medium">{order.items?.length || 0}</span> items
                                {order.catering_plan && <span className="ml-1">| {order.catering_plan}</span>}
                              </div>
                            </div>
                            <div className="ml-2 flex-shrink-0">
                              {isExpanded ? (
                                <ChevronUp className="w-5 h-5 text-slate-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                          </button>

                          {/* Expanded Order Details */}
                          {isExpanded && (
                            <div className="border-t border-slate-200 bg-slate-50/50" data-testid={`order-details-${order.id}`}>
                              {/* Monday Items */}
                              {order.items?.filter(i => i.delivery_day === 'Monday').length > 0 && (
                                <div className="p-4 pb-2">
                                  <div className="flex items-center gap-2 mb-3">
                                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                      <span className="text-blue-700 font-semibold text-xs">M</span>
                                    </div>
                                    <h4 className="font-medium text-sm text-slate-900">Monday Delivery</h4>
                                  </div>
                                  <div className="space-y-2">
                                    {order.items.filter(i => i.delivery_day === 'Monday').map((item, idx) => {
                                      const realIdx = order.items.findIndex(i => i === item);
                                      const report = getItemReport(order, realIdx);
                                      return (
                                        <OrderItemRow 
                                          key={`${item.product_id}-${realIdx}`} 
                                          item={item} 
                                          itemIndex={realIdx}
                                          orderId={order.id}
                                          report={report}
                                          reportingItem={reportingItem}
                                          setReportingItem={setReportingItem}
                                          onReport={handleReportItem}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Wednesday Items */}
                              {order.items?.filter(i => i.delivery_day === 'Wednesday').length > 0 && (
                                <div className="p-4 pb-2">
                                  <div className="flex items-center gap-2 mb-3">
                                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                                      <span className="text-purple-700 font-semibold text-xs">W</span>
                                    </div>
                                    <h4 className="font-medium text-sm text-slate-900">Wednesday Delivery</h4>
                                  </div>
                                  <div className="space-y-2">
                                    {order.items.filter(i => i.delivery_day === 'Wednesday').map((item, idx) => {
                                      const realIdx = order.items.findIndex(i => i === item);
                                      const report = getItemReport(order, realIdx);
                                      return (
                                        <OrderItemRow 
                                          key={`${item.product_id}-${realIdx}`} 
                                          item={item} 
                                          itemIndex={realIdx}
                                          orderId={order.id}
                                          report={report}
                                          reportingItem={reportingItem}
                                          setReportingItem={setReportingItem}
                                          onReport={handleReportItem}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Items without delivery day (legacy) */}
                              {order.items?.filter(i => !i.delivery_day || (i.delivery_day !== 'Monday' && i.delivery_day !== 'Wednesday')).length > 0 && (
                                <div className="p-4 pb-2">
                                  <h4 className="font-medium text-sm text-slate-900 mb-3">Items</h4>
                                  <div className="space-y-2">
                                    {order.items.filter(i => !i.delivery_day || (i.delivery_day !== 'Monday' && i.delivery_day !== 'Wednesday')).map((item, idx) => {
                                      const realIdx = order.items.findIndex(i => i === item);
                                      const report = getItemReport(order, realIdx);
                                      return (
                                        <OrderItemRow 
                                          key={`${item.product_id}-${realIdx}`} 
                                          item={item} 
                                          itemIndex={realIdx}
                                          orderId={order.id}
                                          report={report}
                                          reportingItem={reportingItem}
                                          setReportingItem={setReportingItem}
                                          onReport={handleReportItem}
                                        />
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Add-ons */}
                              {(order.basic_addons?.length > 0 || order.premium_addons?.length > 0) && (
                                <div className="p-4 pt-2">
                                  <h4 className="font-medium text-sm text-slate-900 mb-2">Add-ons</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {order.basic_addons?.map((addon) => (
                                      <Badge key={`basic-${addon}`} variant="secondary" className="text-xs">{addon}</Badge>
                                    ))}
                                    {order.premium_addons?.map((addon) => (
                                      <Badge key={`premium-${addon}`} className="bg-purple-100 text-purple-700 text-xs">{addon} (Premium)</Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Notes */}
                              {order.notes && (
                                <div className="p-4 pt-0">
                                  <h4 className="font-medium text-sm text-slate-900 mb-1">Notes</h4>
                                  <p className="text-sm text-slate-600">{order.notes}</p>
                                </div>
                              )}

                              {/* Reorder Button */}
                              <div className="p-4 pt-2 border-t border-slate-200 flex flex-wrap gap-2">
                                {isOrderEditable(order) ? (
                                  <Button
                                    onClick={(e) => { e.stopPropagation(); handleEditOrder(order); }}
                                    className="rounded-full bg-blue-600 hover:bg-blue-700 active:scale-95 transition-all duration-200"
                                    size="sm"
                                    data-testid={`edit-order-button-${order.id}`}
                                  >
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit Order
                                  </Button>
                                ) : (
                                  <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 rounded-full px-3 py-1.5" data-testid={`edit-deadline-passed-${order.id}`}>
                                    <Lock className="w-3.5 h-3.5" />
                                    <span>Editing deadline passed (Thu 2pm UK)</span>
                                  </div>
                                )}
                                <Button
                                  onClick={(e) => { e.stopPropagation(); handleReorder(order); }}
                                  variant="outline"
                                  className="rounded-full active:scale-95 transition-all duration-200"
                                  size="sm"
                                  data-testid={`reorder-button-${order.id}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Reorder This
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </ClientLayout>
  );
};

const OrderItemRow = ({ item, itemIndex, orderId, report, reportingItem, setReportingItem, onReport }) => {
  const isReporting = reportingItem === `${orderId}-${itemIndex}`;
  const reportKey = `${orderId}-${itemIndex}`;
  const [reportNotes, setReportNotes] = useState('');

  const getReportStatusBadge = (status) => {
    switch (status) {
      case 'approved': return { className: 'bg-green-100 text-green-700', label: 'Credit Approved' };
      case 'disapproved': return { className: 'bg-red-100 text-red-700', label: 'Credit Declined' };
      default: return { className: 'bg-yellow-100 text-yellow-700', label: 'Under Review' };
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm text-slate-900 break-words">{item.product_name}</p>
          <p className="text-xs text-slate-500">{item.category_name?.split(':')[0]}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge variant="secondary" className="text-xs">x{item.quantity}</Badge>
          {report ? (
            <Badge className={`text-xs ${report.report_type === 'missing' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>
              {report.report_type === 'missing' ? 'Missing' : 'Damaged'}
            </Badge>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setReportingItem(isReporting ? null : reportKey); setReportNotes(''); }}
              className="h-7 px-2 text-xs text-slate-500 hover:text-slate-700"
              data-testid={`report-item-${itemIndex}`}
            >
              <AlertTriangle className="w-3 h-3 mr-1" />
              Report
            </Button>
          )}
        </div>
      </div>

      {/* Credit resolution status */}
      {report && (
        <div className="mt-2 pt-2 border-t border-slate-100 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            {(() => {
              const sb = getReportStatusBadge(report.status);
              return <Badge className={`${sb.className} text-xs`}>{sb.label}</Badge>;
            })()}
            {report.notes && (
              <span className="text-xs text-slate-500 italic truncate max-w-[200px]" title={report.notes}>
                Your note: {report.notes}
              </span>
            )}
          </div>
          {report.admin_comment && (
            <div className="bg-slate-50 rounded-lg px-2.5 py-1.5 text-xs">
              <span className="font-medium text-slate-600">Admin: </span>
              <span className="text-slate-700">{report.admin_comment}</span>
            </div>
          )}
        </div>
      )}

      {isReporting && !report && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          <textarea
            value={reportNotes}
            onChange={(e) => setReportNotes(e.target.value)}
            placeholder="Describe the issue (e.g., 3 of 5 sandwiches missing, packaging damaged)..."
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            data-testid={`report-notes-${itemIndex}`}
          />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline"
              onClick={() => onReport(orderId, itemIndex, 'missing', reportNotes)}
              className="h-8 text-xs rounded-full border-red-200 text-red-600 hover:bg-red-50"
              data-testid={`report-missing-${itemIndex}`}>
              Report Missing
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => onReport(orderId, itemIndex, 'damaged', reportNotes)}
              className="h-8 text-xs rounded-full border-amber-200 text-amber-600 hover:bg-amber-50"
              data-testid={`report-damaged-${itemIndex}`}>
              Report Damaged
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setReportingItem(null)} className="h-8 text-xs">
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
